import { makeAutoObservable, runInAction } from "mobx";
import {
  InventoryStockPartDetailsCoresDto,
  InventoryStockPartDetailsDto,
  InventoryStockCountsDto,
  InventoryStockPartDetailsLocationDto,
  InventoryStockPartDetailsTicketDto,
  InventoryStockPartDetailsVendorDto,
  PurchaseHistoryDto,
  UpdatePartNotificationSettingsDto,
} from "../models/inventory";
import { Pagination, PagingParams } from "../models/pagination";
import agent from "../api/agent";

export default class InventoryPartDetailsStore {
  loadingInitial = false;

  constructor() {
    makeAutoObservable(this);
  }

  partDetailsPredicate = new Map().set("all", true);
  selectedPartDetails: InventoryStockPartDetailsDto | undefined = undefined;
  selectedPartDetailsVendors = new Map<
    string | undefined,
    InventoryStockPartDetailsVendorDto
  >();
  selectedPartDetailsLocations = new Map<
    string | undefined,
    InventoryStockPartDetailsLocationDto
  >();
  selectedPartDetailsTickets = new Map<
    string | undefined,
    InventoryStockPartDetailsTicketDto
  >();
  selectedPartDetailsPurchaseHistory = new Map<
    string | undefined,
    PurchaseHistoryDto
  >();
  selectedPartDetailsCores = new Map<
    string | undefined,
    InventoryStockPartDetailsCoresDto
  >();
  selectedPartCounts = new Map<string, InventoryStockCountsDto>();
  partDetailsVendorsPagingParams = new PagingParams();
  partDetailsLocationPagingParams = new PagingParams();
  partDetailsTicketsPagingParams = new PagingParams();
  purchaseHistoryPagingParams = new PagingParams(1, 25);
  partDetailsCoresPagingParams = new PagingParams();
  partDetailsVendorsPagination: Pagination | null = null;
  partDetailsLocationPagination: Pagination | null = null;
  partDetailsTicketsPagination: Pagination | null = null;
  purchaseHistoryPagination: Pagination | null = null;
  partDetailsCoresPagination: Pagination | null = null;

  // Part Details
  setSelectedPartDetails = (values: InventoryStockPartDetailsDto) => {
    this.selectedPartDetails = values;
  };

  // Part Details Vendors
  get partDetailsVendorsAxiosParams() {
    const params = new URLSearchParams();
    params.append(
      "pageNumber",
      this.partDetailsVendorsPagingParams.pageNumber.toString()
    );
    params.append(
      "pageSize",
      this.partDetailsVendorsPagingParams.pageSize.toString()
    );
    this.partDetailsPredicate.forEach((value, key) =>
      params.append(key, value)
    );

    return params;
  }
  setSelectedPartDetailsVendors = (
    id: string,
    values: InventoryStockPartDetailsVendorDto
  ) => {
    this.selectedPartDetailsVendors.set(id, values);
  };
  setPartDetailsVendorPagingParams = (pagingParams: PagingParams) =>
    (this.partDetailsVendorsPagingParams = pagingParams);
  setPartDetailsVendorPagination = (pagination: Pagination) =>
    (this.partDetailsVendorsPagination = pagination);
  resetPartDetailsVendorsRegistry = () =>
    this.selectedPartDetailsVendors.clear();
  get getSelectedPartDetailsVendors() {
    return Array.from(this.selectedPartDetailsVendors.values());
  }

  // Part Details Locations
  get partDetailsLocationsAxiosParams() {
    const params = new URLSearchParams();
    params.append(
      "pageNumber",
      this.partDetailsLocationPagingParams.pageNumber.toString()
    );
    params.append(
      "pageSize",
      this.partDetailsLocationPagingParams.pageSize.toString()
    );
    this.partDetailsPredicate.forEach((value, key) =>
      params.append(key, value)
    );

    return params;
  }
  setSelectedPartDetailsLocations = (
    id: string,
    values: InventoryStockPartDetailsLocationDto
  ) => {
    this.selectedPartDetailsLocations.set(id, values);
  };
  setPartDetailsLocationPagingParams = (pagingParams: PagingParams) =>
    (this.partDetailsLocationPagingParams = pagingParams);
  setPartDetailsLocationPagination = (pagination: Pagination) =>
    (this.partDetailsLocationPagination = pagination);
  resetPartDetailsLocationsRegistry = () =>
    this.selectedPartDetailsLocations.clear();
  get getSelectedPartDetailsLocations() {
    return Array.from(this.selectedPartDetailsLocations.values());
  }
  // Part Details Cores
  get partDetailsCoresAxiosParams() {
    const params = new URLSearchParams();
    params.append(
      "pageNumber",
      this.partDetailsCoresPagingParams.pageNumber.toString()
    );
    params.append(
      "pageSize",
      this.partDetailsCoresPagingParams.pageSize.toString()
    );

    return params;
  }
  setSelectedPartDetailsCores = (
    id: string,
    values: InventoryStockPartDetailsCoresDto
  ) => {
    this.selectedPartDetailsCores.set(id, values);
  };
  setPartDetailsCoresPagingParams = (pagingParams: PagingParams) =>
    (this.partDetailsCoresPagingParams = pagingParams);
  setPartDetailsCoresPagination = (pagination: Pagination) =>
    (this.partDetailsCoresPagination = pagination);
  resetPartDetailsCoresRegistry = () => this.selectedPartDetailsCores.clear();
  get getSelectedPartDetailsCores() {
    return Array.from(this.selectedPartDetailsCores.values());
  }
  // Part Details Tickets
  get partDetailsTicketsAxiosParams() {
    const params = new URLSearchParams();
    params.append(
      "pageNumber",
      this.partDetailsTicketsPagingParams.pageNumber.toString()
    );
    params.append(
      "pageSize",
      this.partDetailsTicketsPagingParams.pageSize.toString()
    );
    this.partDetailsPredicate.forEach((value, key) =>
      params.append(key, value)
    );

    return params;
  }
  setSelectedPartDetailsTickets = (
    id: string,
    values: InventoryStockPartDetailsTicketDto
  ) => {
    this.selectedPartDetailsTickets.set(id, values);
  };
  setPartDetailsTicketPagingParams = (pagingParams: PagingParams) =>
    (this.partDetailsTicketsPagingParams = pagingParams);
  setPartDetailsTicketsPagination = (pagination: Pagination) =>
    (this.partDetailsTicketsPagination = pagination);
  resetPartDetailsTicketsRegistry = () =>
    this.selectedPartDetailsTickets.clear();
  get getSelectedPartDetailsTickets() {
    return Array.from(this.selectedPartDetailsTickets.values());
  }
  // Part Details Purchase History
  get purchaseHistoryAxiosParams() {
    const params = new URLSearchParams();
    params.append(
      "pageNumber",
      this.purchaseHistoryPagingParams.pageNumber.toString()
    );
    params.append(
      "pageSize",
      this.purchaseHistoryPagingParams.pageSize.toString()
    );

    return params;
  }
  setSelectedPurchaseHistoryItem = (id: string, values: PurchaseHistoryDto) => {
    this.selectedPartDetailsPurchaseHistory.set(id, values);
  };
  setPurchaseHistoryPagingParams = (pagingParams: PagingParams) =>
    (this.purchaseHistoryPagingParams = pagingParams);
  setPurchaseHistoryPagination = (pagination: Pagination) =>
    (this.purchaseHistoryPagination = pagination);
  resetPurchaseHistoryRegistry = () =>
    this.selectedPartDetailsPurchaseHistory.clear();
  get getSelectedPurchaseHistory() {
    return Array.from(this.selectedPartDetailsPurchaseHistory.values());
  }

  loadInventoryPartDetails = async (partId: string) => {
    this.loadingInitial = true;
    try {
      const result =
        await agent.inventoryStockPartDetails.getInventoryPartDetails(partId);
      runInAction(() => {
        this.setSelectedPartDetails(result);
      });
      return result;
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };

  loadInventoryCounts = async (partIds: string[]) => {
    try {
      const result =
        await agent.inventoryStockPartDetails.getInventoryCounts(partIds);
      runInAction(() => {
        this.selectedPartCounts.clear();
        for (let r of result) {
            this.selectedPartCounts.set(r.id, r);
        }
      });
      return result;
    } catch (error) {
      console.log(error);
    } 
  };

  loadInventoryPartVendors = async (partId: string) => {
    this.loadingInitial = true;
    this.resetPartDetailsVendorsRegistry();
    try {
      const result =
        await agent.inventoryStockPartDetails.getInventoryPartVendors(
          partId,
          this.partDetailsVendorsAxiosParams
        );
      runInAction(() => {
        result.data.forEach((dataItem) => {
          this.setSelectedPartDetailsVendors(dataItem.id, dataItem);
        });
        this.setPartDetailsVendorPagination(result.pagination);
      });
      return result;
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };
  loadInventoryPartLocations = async (partId: string) => {
    this.loadingInitial = true;
    this.resetPartDetailsLocationsRegistry();
    try {
      const result =
        await agent.inventoryStockPartDetails.getInventoryPartLocations(
          partId,
          this.partDetailsVendorsAxiosParams
        );
      runInAction(() => {
        result.data.slice(0, 1).forEach((dataItem) => {
          this.setSelectedPartDetailsLocations(dataItem.id, dataItem);
        });
        this.setPartDetailsLocationPagination(result.pagination);
      });
      return result;
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };

  loadInventoryPartCores = async (partId: string) => {
    this.loadingInitial = true;
    this.resetPartDetailsCoresRegistry();
    try {
      const result =
        await agent.inventoryStockPartDetails.getInventoryPartCores(
          partId,
          this.partDetailsVendorsAxiosParams
        );
      runInAction(() => {
        result.data.forEach((dataItem) => {
          this.setSelectedPartDetailsCores(dataItem.id, dataItem);
        });
        this.setPartDetailsVendorPagination(result.pagination);
      });
      return result;
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };
  loadInventoryPartTickets = async (partId: string) => {
    this.loadingInitial = true;
    this.resetPartDetailsTicketsRegistry();
    try {
      const result =
        await agent.inventoryStockPartDetails.getInventoryPartTickets(
          partId,
          this.partDetailsVendorsAxiosParams
        );
      runInAction(() => {
        result.data.forEach((dataItem: InventoryStockPartDetailsTicketDto) => {
          this.setSelectedPartDetailsTickets(dataItem.ticketId, dataItem);
        });
        this.setPartDetailsTicketsPagination(result.pagination);
      });
      return result;
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };
  loadPurchaseHistory = async (partId: string) => {
    this.loadingInitial = true;
    this.resetPurchaseHistoryRegistry();
    try {
      const result = await agent.inventoryStockPartDetails.getPurchaseHistory(
        partId,
        this.purchaseHistoryAxiosParams
      );

      runInAction(() => {
        result.data.forEach((dataItem: PurchaseHistoryDto) =>
          this.setSelectedPurchaseHistoryItem(dataItem.id, dataItem)
        );
      });
      this.setPurchaseHistoryPagination(result.pagination);
      return result;
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };

  updateNotificationSettings = async (
    partId: string,
    values: UpdatePartNotificationSettingsDto
  ) => {
    this.loadingInitial = true;
    try {
      await agent.inventoryStockPartDetails.updateNotificationSettings(
        partId,
        values
      );
    } catch (error) {
      console.log("error:", error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };
}
